/**
 * Hook for fetching and applying workspace branding.
 * Used by:
 * 1. AppThemeProvider on initial load (if token exists)
 * 2. SignIn.tsx after login completes
 *
 * DRY: Single source of truth for all workspace branding logic.
 */

import { useDispatch } from 'react-redux';
import { authClient, workspacesClient } from '../apis/client-instances';
import { setAccount } from '../features/account/accountSlice';
import { getFileUrl } from './function';

interface AccountData {
  accountId: number | null;
  name: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  domain: string | null;
  bannerHtml?: string | null;
}

export const useWorkspaceBranding = () => {
  const dispatch = useDispatch();

  /**
   * Re-fetches canonical account branding (ignores any workspace overlay) and
   * applies it to Redux + document title/favicon. Used on logout and before
   * sign-in to strip stale workspace branding from the previous session.
   */
  const resetToAccountBranding = async (): Promise<AccountData | null> => {
    try {
      const account = await authClient.getAccountByDomain();
      const accountData: AccountData = {
        accountId: account.id,
        name: account.name,
        logoUrl: account.logoUrl,
        primaryColor: account.primaryColor,
        domain: account.domain,
        bannerHtml: account.bannerHtml || null,
      };

      dispatch(setAccount({
        ...accountData,
        // Account-level favicon resets too — clears any workspace
        // favicon left from a previous session.
        favicon: account.favicon || null,
        bannerImageUrl: null,
        dashboardWidgetHtml: null,
      }));

      if (account.name) {
        document.title = `${account.name} - LMS`;
      }
      document.getElementById('favicon')?.setAttribute('href', account.favicon || '/favicon.ico');

      return accountData;
    } catch (err) {
      console.warn('Failed to reset to account branding:', err);
      return null;
    }
  };

  /**
   * Fetches workspace branding and applies it to Redux.
   * Merges workspace branding over account branding.
   * @param accountData - Current account data to merge with (passed to avoid stale Redux state)
   * @returns true if workspace branding was applied, false otherwise
   */
  const fetchAndApplyWorkspaceBranding = async (accountData: AccountData): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const workspace = await workspacesClient.getMyWorkspaceBranding();

      // Apply the workspace overlay if the workspace has ANY
      // customization the learner-facing surfaces depend on — visual
      // (color/logo/favicon) OR dashboard (banner/widget). Otherwise
      // a workspace whose admin only configured a dashboard widget
      // would have its widget silently dropped at login.
      if (
        workspace &&
        (workspace.primaryColor ||
          workspace.logoUrl ||
          workspace.favicon ||
          workspace.bannerHtml ||
          workspace.bannerImageUrl ||
          workspace.dashboardWidgetHtml)
      ) {
        // Get signed URL for workspace logo if it exists
        let finalLogoUrl = accountData.logoUrl;
        if (workspace.logoUrl) {
          try {
            finalLogoUrl = await getFileUrl(workspace.logoUrl);
          } catch {
            finalLogoUrl = workspace.logoUrl;
          }
        }

        const finalPrimaryColor = workspace.primaryColor || accountData.primaryColor;
        const finalName = workspace.name || accountData.name;
        // Resolved workspace favicon — ONLY populated when the
        // workspace actually uploaded one. This goes into Redux for
        // SidebarLogo's collapsed-rail icon, which should only swap
        // when the admin specifically asked for it. The browser-tab
        // favicon below has a logo fallback; the sidebar does not.
        let resolvedWorkspaceFavicon: string | null = null;
        if (workspace.favicon) {
          try {
            const r = await getFileUrl(workspace.favicon);
            resolvedWorkspaceFavicon = r || workspace.favicon;
          } catch {
            resolvedWorkspaceFavicon = workspace.favicon;
          }
        }

        console.log('Applying workspace branding:', { primaryColor: finalPrimaryColor, logoUrl: finalLogoUrl });

        // Update Redux with merged branding (including dashboard customization)
        dispatch(setAccount({
          accountId: accountData.accountId,
          name: finalName,
          logoUrl: finalLogoUrl,
          favicon: resolvedWorkspaceFavicon,
          primaryColor: finalPrimaryColor,
          domain: accountData.domain,
          bannerHtml: workspace.bannerHtml || accountData.bannerHtml || null,
          bannerImageUrl: workspace.bannerImageUrl || null,
          dashboardWidgetHtml: workspace.dashboardWidgetHtml || null,
        }));

        // Browser-tab favicon: prefer explicit favicon, fall back to
        // the workspace logo (so the tab icon is at least tenant-y).
        const finalFavicon = resolvedWorkspaceFavicon || finalLogoUrl;

        // Update page title
        if (finalName) {
          document.title = `${finalName} - LMS`;
        }

        // Update favicon
        if (finalFavicon) {
          document.getElementById('favicon')?.setAttribute('href', finalFavicon);
        }

        return true;
      }

      return false;
    } catch (err) {
      console.warn('Failed to fetch workspace branding:', err);
      return false;
    }
  };

  return { fetchAndApplyWorkspaceBranding, resetToAccountBranding };
};
